import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { DollarSign } from 'lucide-react';
import apiService from '../../services/api';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { QuotationItem } from '../../types';

interface CatalogItemPriceUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: QuotationItem | null;
  onSaved: () => void;
}

const CatalogItemPriceUpdateModal: React.FC<CatalogItemPriceUpdateModalProps> = ({
  isOpen,
  onClose,
  item,
  onSaved,
}) => {
  const [purchasePrice, setPurchasePrice] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      const value = (item as any).purchasePrice ?? item.unitPrice;
      setPurchasePrice(value != null ? String(value) : '');
    } else {
      setPurchasePrice('');
    }
  }, [item, isOpen]);

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!item?._id) return;
    const num = parseFloat(purchasePrice);
    if (!Number.isFinite(num) || num < 0) {
      toast.error('Please enter a valid purchase price.');
      return;
    }
    setSaving(true);
    try {
      await apiService.updateQuotationItem(item._id, { purchasePrice: num });
      toast.success('Purchase price updated successfully');
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Failed to update purchase price');
    } finally {
      setSaving(false);
    }
  };

  const itemName = item?.name || 'Catalog item';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Update purchase price"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            icon={<DollarSign className="w-4 h-4" />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSave} className="space-y-4">
        <p className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">{itemName}</span>
        </p>
        <Input
          label="Purchase price"
          type="number"
          min={0}
          step={0.01}
          value={purchasePrice}
          onChange={(e) => setPurchasePrice(e.target.value)}
          placeholder="0.00"
        />
      </form>
    </Modal>
  );
};

export default CatalogItemPriceUpdateModal;
